import React, { useState } from 'react'
import { useAuth } from '../../Context/AuthContext'
import { Link, useNavigate } from 'react-router-dom'
import DarkAuthInput from '../../Components/Inputs/DarkAuthInput'
import logo from '../../Assets/new-logo.png'
import { Star, Award } from 'react-feather'

const LoginScreen = () => {
  const navigate = useNavigate()
  const { authLoading, signInUser, validLogin } = useAuth()

  const [username, setUsername] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showStars, setShowStars] = useState<boolean>(true)

  // Hide stars after initial animation
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowStars(false)
    }, 3000)
    return () => clearTimeout(timer)
  }, [])

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value)
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
  }

  const loginAUser = (e: React.FormEvent) => {
    e.preventDefault()
    signInUser({ username, password })
  }

  const goToHome = () => {
    navigate('/auth/accessCode')
  }

  return (
    <div className="min-h-screen w-full bg-dark-bg flex items-center justify-center p-4 pt-safe-top pb-safe-bottom relative overflow-hidden">
      {/* Background gradient orbs - smaller on mobile */}
      <div className="absolute top-0 left-0 w-48 md:w-96 h-48 md:h-96 bg-accent-purple/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 animate-float" />
      <div className="absolute bottom-0 right-0 w-48 md:w-96 h-48 md:h-96 bg-accent-gold/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 animate-float-slow" />
      <div className="absolute top-1/2 right-1/4 w-32 md:w-64 h-32 md:h-64 bg-accent-pink/10 rounded-full blur-3xl animate-float-slower" />

      {/* Main card */}
      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        {/* Logo and header */}
        <div className="text-center mb-6 md:mb-8">
          {/* Logo with Anniversary Badge */}
          <div className="inline-flex items-center justify-center gap-2 md:gap-3 mb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-dark-card rounded-2xl border border-dark-border animate-bounce-subtle">
              <img className="h-10 w-10 md:h-14 md:w-14 object-contain" src={logo} alt="Logo" />
            </div>

            {/* 2 Year Anniversary Badge - Hidden after January 2027 */}
            {new Date() < new Date('2027-02-01') && (
              <div className="relative group">
                {/* Floating stars - fade out after initial load */}
                <div className={`absolute -top-2 -left-1 animate-bounce-subtle transition-opacity duration-500 ${showStars ? 'opacity-100' : 'opacity-0'}`}>
                  <Star className="w-3 h-3 text-accent-gold fill-accent-gold" />
                </div>
                <div className={`absolute -top-1 -right-2 animate-bounce-subtle transition-opacity duration-500 ${showStars ? 'opacity-100' : 'opacity-0'}`} style={{ animationDelay: '0.5s' }}>
                  <Star className="w-2.5 h-2.5 text-accent-gold fill-accent-gold" />
                </div>
                <div className={`absolute -bottom-1 left-0 animate-bounce-subtle transition-opacity duration-500 ${showStars ? 'opacity-100' : 'opacity-0'}`} style={{ animationDelay: '1s' }}>
                  <Star className="w-2 h-2 text-accent-teal fill-accent-teal" />
                </div>

                {/* Anniversary badge with colorful outline on hover */}
                <div className="p-[2px] rounded-2xl bg-dark-border group-hover:bg-gradient-to-r group-hover:from-accent-purple group-hover:via-accent-pink group-hover:to-accent-gold group-hover:scale-105 transition-all duration-300 shadow-lg group-hover:shadow-accent-purple/30">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-accent-gold to-accent-orange group-hover:bg-dark-card group-hover:from-dark-card group-hover:to-dark-card rounded-[14px] flex flex-col items-center justify-center px-1 transition-all duration-300">
                    <span className="text-2xl md:text-3xl font-black text-white leading-none group-hover:bg-gradient-to-r group-hover:from-accent-purple group-hover:via-accent-pink group-hover:to-accent-gold group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">2</span>
                    <p className="text-[6px] md:text-[7px] font-bold text-white/90 uppercase tracking-tight text-center leading-tight mt-0.5">Year Anniversary</p>
                  </div>
                </div>

                {/* Tooltip on hover */}
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-20">
                  <div className="p-[1px] rounded-xl bg-gradient-to-r from-accent-purple via-accent-pink to-accent-gold">
                    <div className="bg-dark-card rounded-[11px] px-3 py-2.5">
                      <p className="text-[11px] text-gray-300 text-center leading-relaxed">
                        Celebrating <span className="text-accent-gold font-semibold">2 years</span> of Intellasure. Thank you for guiding lives to recovery.
                      </p>
                    </div>
                  </div>
                  {/* Arrow */}
                  <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-accent-pink rotate-45" />
                </div>
              </div>
            )}
          </div>

          <h1 className="text-2xl md:text-3xl font-black text-white mb-2">Welcome back</h1>
          <p className="text-gray-400 text-sm md:text-base">Sign in to continue to your dashboard</p>
        </div>

        {/* Login form card */}
        <div className="bg-dark-card border border-dark-border rounded-2xl p-5 md:p-8 shadow-2xl animate-scale-in">
          {!validLogin && (
            <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
              <p className="text-sm text-red-400 text-center">
                Username/Password don't match our records
              </p>
            </div>
          )}

          <form onSubmit={loginAUser}>
            <DarkAuthInput
              value={username}
              handleFunction={handleUsernameChange}
              placeholder="Enter your email"
              type="text"
              icon="email"
            />

            <DarkAuthInput
              value={password}
              handleFunction={handlePasswordChange}
              placeholder="Enter your password"
              type={showPassword ? 'text' : 'password'}
              icon="password"
              showPasswordToggle={true}
              onTogglePassword={() => setShowPassword(!showPassword)}
              isPasswordVisible={showPassword}
            />

            <div className="flex justify-end mb-6">
              <Link
                to="/auth/forgotPassword"
                className="text-sm text-accent-teal hover:text-accent-teal/80 transition-colors"
              >
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={authLoading}
              className="w-full py-4 md:py-3.5 px-4 bg-gradient-to-r from-accent-teal to-accent-deep-purple
                         text-white font-semibold rounded-xl text-base md:text-sm
                         hover:opacity-90 active:opacity-80 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200
                         disabled:opacity-50 disabled:cursor-not-allowed
                         shadow-lg shadow-accent-teal/25 hover:shadow-xl hover:shadow-accent-teal/30"
            >
              {authLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-dark-border" />
            <span className="text-gray-500 text-sm">or</span>
            <div className="flex-1 h-px bg-dark-border" />
          </div>

          {/* Create account link */}
          <p className="text-center text-gray-400 text-sm">
            Don't have an account?{' '}
            <span
              onClick={goToHome}
              className="text-accent-gold hover:text-accent-gold-light cursor-pointer font-medium transition-colors"
            >
              Create Account
            </span>
          </p>
        </div>

        {/* Footer stats */}
        <div className="mt-8 flex justify-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="bg-gradient-to-r from-accent-purple/20 to-accent-gold/20 rounded-xl px-6 py-3 border border-dark-border hover:border-accent-teal/30 transition-colors duration-300">
            <p className="text-gray-400 text-sm text-center">
              Trusted by <span className="text-white font-semibold">1,000+</span> healthcare professionals
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginScreen
